// src/pages/Admin/AdherentPret/AddAdherentPretModal.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FaSave, FaTimes, FaUser, FaBox, FaCalendarAlt } from "react-icons/fa";
import { createAdherentPret } from "../../../services/adherentpretServices";
import { getAllAdherentsList } from "../../../services/adherentServices";
import { getArticles } from "../../../services/articlesServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";

const todayStr = () => new Date().toISOString().slice(0, 10);
const defaultForm = () => ({
  adherent_id: "",
  article_id: "",
  date_pret: todayStr(),
  note_pret: "",
});

const normalize = (s) =>
  (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function AddAdherentPretModal({ isOpen, onClose, refreshList }) {
  const [formData, setFormData] = useState(defaultForm());
  const [adherents, setAdherents] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadLists = useCallback(async () => {
    setLoadingLists(true);
    try {
      const [adherentsData, articlesData] = await Promise.all([
        getAllAdherentsList(),
        getArticles(),
      ]);

      const listAdh = Array.isArray(adherentsData) ? adherentsData : [];
      // Trie simple par nom/prénom pour une liste plus agréable
      listAdh.sort((a, b) =>
        `${a.nom || ""} ${a.prenom || ""}`.localeCompare(
          `${b.nom || ""} ${b.prenom || ""}`,
          "fr",
          { sensitivity: "base" }
        )
      );
      setAdherents(listAdh);

      // Articles uniquement catégorie "Prêt" (robuste aux accents/majuscules)
      const filtered =
        Array.isArray(articlesData) && articlesData.length
          ? articlesData.filter(
              (a) => normalize(a?.categorie) === "pret"
            )
          : [];
      // Trie par nom
      filtered.sort((a, b) =>
        (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })
      );
      setArticles(filtered);
    } catch (e) {
      console.error(e);
      toast.error("Erreur chargement adhérents ou articles");
    } finally {
      setLoadingLists(false);
    }
  }, []);

  // Reset + chargement à l’ouverture
  useEffect(() => {
    if (!isOpen) return;
    setFormData(defaultForm());
    loadLists();
  }, [isOpen, loadLists]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedArticle = useMemo(
    () => articles.find((a) => String(a.id) === String(formData.article_id)),
    [articles, formData.article_id]
  );

  const canSubmit =
    !loadingLists &&
    !submitting &&
    formData.adherent_id &&
    formData.article_id &&
    formData.date_pret;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        adherent_id: Number(formData.adherent_id),
        article_id: Number(formData.article_id),
      };
      await createAdherentPret(payload);
      toast.success("Prêt enregistré avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la création du prêt", error);
      toast.error("Erreur lors de la création du prêt");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
            Nouveau prêt de matériel
          </h3>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          {/* Adhérent */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              <span className="inline-flex items-center gap-2">
                <FaUser /> Adhérent
              </span>
            </label>
            <select
              name="adherent_id"
              value={formData.adherent_id}
              onChange={handleChange}
              disabled={loadingLists}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            >
              <option value="">
                {loadingLists ? "Chargement…" : "— Sélectionner un adhérent —"}
              </option>
              {adherents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nom} {a.prenom}
                </option>
              ))}
            </select>
          </div>

          {/* Article */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              <span className="inline-flex items-center gap-2">
                <FaBox /> Matériel (catégorie “Prêt”)
              </span>
            </label>
            <select
              name="article_id"
              value={formData.article_id}
              onChange={handleChange}
              disabled={loadingLists}
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              required
            >
              <option value="">
                {loadingLists ? "Chargement…" : "— Sélectionner un matériel —"}
              </option>
              {articles.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nom} {a.categorie ? `(${a.categorie})` : ""}
                </option>
              ))}
            </select>

            {/* Aperçu article choisi (si image dispo) */}
            {selectedArticle?.photo_url && (
              <div className="mt-3 flex items-center gap-3">
                <img
                  src={selectedArticle.photo_url}
                  alt={selectedArticle.nom}
                  className="h-14 w-14 rounded-lg object-cover ring-2 ring-slate-200 dark:ring-slate-700"
                />
                <div className="text-sm">
                  <div className="font-medium text-slate-800 dark:text-slate-100">
                    {selectedArticle.nom}
                  </div>
                  <div className="text-slate-500 dark:text-slate-400 text-xs">
                    {selectedArticle.categorie || "—"}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Date + Note */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                <span className="inline-flex items-center gap-2">
                  <FaCalendarAlt /> Date de prêt
                </span>
              </label>
              <input
                type="date"
                name="date_pret"
                value={formData.date_pret}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Note (optionnelle)
              </label>
              <input
                type="text"
                name="note_pret"
                placeholder="Ex : taille S / housse incluse …"
                value={formData.note_pret}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${!canSubmit ? "opacity-60 pointer-events-none" : ""}`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
